import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import SEO from "@/components/app/SEO";

const fmtEur = (cents) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format((cents || 0) / 100);
const fmtNum = (n) => new Intl.NumberFormat("fr-FR").format(n || 0);
const fmtPct = (p) => `${(p || 0).toFixed(1)} %`;

export default function AdminAnalyticsPage() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/analytics/summary?days=${days}`).then(({ data }) => setData(data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [days]);

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <SEO title="Analytics et conversions" noindex />
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">Analytics & Conversions</h1>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="border border-border rounded-md px-3 py-1.5 text-sm bg-background" data-testid="analytics-period">
          <option value={7}>7 jours</option>
          <option value={30}>30 jours</option>
          <option value={90}>90 jours</option>
          <option value={365}>1 an</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Chargement…</div>
      ) : !data ? (
        <p className="text-sm text-muted-foreground">Aucune donnée disponible pour cette période.</p>
      ) : (
        <>
          <section>
            <h2 className="text-lg font-semibold mb-3">Commercial (tests / admin / internes exclus)</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Metric label="CA aujourd'hui" value={fmtEur(data.commercial.revenue_today_cents)} testid="rev-today" />
              <Metric label="CA 7 derniers jours" value={fmtEur(data.commercial.revenue_7d_cents)} testid="rev-7d" />
              <Metric label="CA 30 derniers jours" value={fmtEur(data.commercial.revenue_30d_cents)} testid="rev-30d" />
              <Metric label={`CA sur ${data.period_days} j`} value={fmtEur(data.commercial.revenue_cents)} highlight testid="revenue" />
              <Metric label="Transactions PayPal réussies" value={fmtNum(data.commercial.revenue_count)} testid="pay-count" />
              <Metric label="Panier moyen" value={fmtEur(data.commercial.avg_basket_cents)} testid="avg-basket" />
              <Metric label="Clients payants (total)" value={fmtNum(data.commercial.paying_clients_total)} testid="paying-total" />
              <Metric label="Clients payants (30j)" value={fmtNum(data.commercial.paying_clients_30d)} testid="paying-30d" />
              <Metric label="Dossiers créés (période)" value={fmtNum(data.commercial.cases_created_period)} testid="cases-created" />
              <Metric label="Checkout démarrés" value={fmtNum(data.funnel.checkout_started)} testid="checkout-started" />
              <Metric label="Remboursements" value={`${fmtEur(data.commercial.refund_cents)} · ${data.commercial.refund_count}`} testid="refunds" />
              <Metric label="Comptes internes exclus" value={fmtNum(data.commercial.excluded_user_count)} testid="excluded" />
            </div>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3">Funnel</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {Object.entries(data.funnel).map(([k, v]) => (
                <Metric key={k} label={k.replaceAll("_", " ")} value={fmtNum(v)} testid={`f-${k}`} />
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3">Taux de conversion</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {Object.entries(data.rates).map(([k, v]) => (
                <Metric key={k} label={k.replaceAll("_", " → ")} value={fmtPct(v)} testid={`r-${k}`} />
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3">Attribution (source / medium / campaign)</h2>
            {data.attribution.length === 0 ? (
              <p className="text-sm text-muted-foreground">Aucune donnée UTM pour cette période.</p>
            ) : (
              <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/50">
                    <tr>
                      <th className="text-left px-4 py-2">Source</th>
                      <th className="text-left px-4 py-2">Medium</th>
                      <th className="text-left px-4 py-2">Campagne</th>
                      <th className="text-right px-4 py-2">Événements</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.attribution.map((r, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="px-4 py-2 font-mono">{r.source || "—"}</td>
                        <td className="px-4 py-2 font-mono">{r.medium || "—"}</td>
                        <td className="px-4 py-2 font-mono">{r.campaign || "—"}</td>
                        <td className="px-4 py-2 text-right font-mono">{fmtNum(r.events)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3">Catalogue</h2>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <Metric label="Organismes" value={fmtNum(data.catalog.organizations)} testid="c-orgs" />
              <Metric label="Guides" value={fmtNum(data.catalog.guides)} testid="c-guides" />
              <Metric label="Blog" value={fmtNum(data.catalog.blog_articles)} testid="c-blog" />
              <Metric label="Utilisateurs" value={fmtNum(data.catalog.users)} testid="c-users" />
              <Metric label="Dossiers" value={fmtNum(data.catalog.cases)} testid="c-cases" />
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function Metric({ label, value, highlight, testid }) {
  return (
    <Card data-testid={testid} className={highlight ? "border-primary" : ""}>
      <CardContent className="p-3">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="text-lg font-bold mt-1 font-mono">{value}</div>
      </CardContent>
    </Card>
  );
}
